import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { FilePlus } from "@phosphor-icons/react";
import api from "@/lib/api";
import { inboxChanged } from "@/components/layout/useInboxCount";

const BTN = "border-2 border-black px-2 py-1 text-[10px] uppercase tracking-widest disabled:opacity-40 transition-colors duration-100";

function wants(r) {
  return r.kind === "own_page" ? `a page of their own: ${r.funnel_title}` : `leads from the shared ${r.funnel_title} page`;
}

function RequestRow({ r, busy, onAct }) {
  const reject = () => {
    const note = window.prompt("Reason (the buyer will see this):", "");
    if (note !== null) onAct(r, "reject", { note: note || null }, "Request declined");
  };
  return (
    <div className="flex flex-wrap items-start gap-2 border-t border-amber-300 py-2 text-[11px]" data-testid={`page-request-${r.id}`}>
      <div className="min-w-0 flex-1">
        <div><strong>{r.firm_name || r.code}</strong> wants {wants(r)}</div>
        {r.note && <div className="text-zinc-700" data-testid={`page-request-note-${r.id}`}>&ldquo;{r.note}&rdquo;</div>}
        <div className="text-[10px] text-zinc-500">{r.submitted_by?.email} · {new Date(r.submitted_at).toLocaleString()}</div>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {r.kind === "own_page" ? (
          <>
            <button type="button" disabled={busy} onClick={() => onAct(r, "approve", { mode: "publish" }, "Page built and published")}
                    className={`${BTN} bg-black text-white hover:bg-emerald-700`} data-testid={`page-request-publish-${r.id}`}>Publish now</button>
            <button type="button" disabled={busy} onClick={() => onAct(r, "approve", { mode: "draft" }, "Page built as a draft")}
                    className={`${BTN} bg-white hover:bg-zinc-100`} data-testid={`page-request-draft-${r.id}`}>Build as draft</button>
          </>
        ) : (
          <button type="button" disabled={busy} onClick={() => onAct(r, "approve", {}, "Shared page added")}
                  className={`${BTN} bg-black text-white hover:bg-emerald-700`} data-testid={`page-request-approve-${r.id}`}>Approve</button>
        )}
        <button type="button" disabled={busy} onClick={reject} className={`${BTN} bg-white hover:bg-zinc-100`}
                data-testid={`page-request-reject-${r.id}`}>Say no</button>
      </div>
    </div>
  );
}

// Buyers' page requests waiting for an admin, each approvable in one click.
export function PageRequestsPanel({ onChanged }) {
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => {
    api.get("/admin/page-requests").then((r) => setRows(r.data.requests || [])).catch(() => setRows([]));
  }, []);
  useEffect(() => { load(); }, [load]);
  if (!rows?.length) return null;
  const onAct = async (r, action, body, ok) => {
    setBusy(true);
    try {
      await api.post(`/admin/page-requests/${r.id}/${action}`, body);
      toast.success(ok);
      inboxChanged();
      load();
      onChanged?.();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Couldn't update the request");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="mb-3 border-2 border-amber-600 bg-amber-50 px-3 pt-2" data-testid="page-requests-panel">
      <div className="label-mono mb-1 flex items-center gap-1.5">
        <FilePlus size={12} weight="bold" /> Page requests waiting <span data-testid="page-requests-count">({rows.length})</span>
      </div>
      {rows.map((r) => <RequestRow key={r.id} r={r} busy={busy} onAct={onAct} />)}
    </div>
  );
}
